import { ChevronDown, type LucideIcon } from 'lucide-react';
import type { ReactElement, ReactNode } from 'react';

import { Related } from './entities.js';
import { Panel } from './panel.js';
import { ActionMenu, ChipFilter, Tabs } from './patterns.js';

import './fiche.css';

/**
 * Mise en page des fiches et des pages métier (refonte ergonomique 0.4.0) : deux colonnes
 * « contenu + résumé » sur grand écran (une seule au téléphone), blocs du résumé, faits clés,
 * sections repliables, valeurs lisibles. Les onglets, menus d'actions, pastilles et blocs
 * « Lié à » sont ceux des patrons communs (`patterns.tsx`, `entities.tsx`) : les noms
 * ci-dessous (`FicheTabs`, `MoreMenu`, `FilterPills`, `RelatedLinks`) n'en sont que des
 * adaptateurs, gardés pour les écrans déjà écrits (docs/ux/composants.md).
 */

export interface FicheTab {
  /** Identifiant de l'onglet, repris dans l'adresse (`?onglet=`). */
  key: string;
  label: string;
  icon?: LucideIcon | undefined;
  /** Compteur affiché à côté du libellé (masqué si indéfini). */
  count?: number | undefined;
  /** Pastille d'alerte (points à traiter dans l'onglet). */
  alert?: boolean | undefined;
  content: ReactNode;
}

/**
 * Onglets d'une fiche = `Tabs` des patrons en montage « visited » : onglet courant dans
 * l'adresse, un onglet n'est monté qu'à sa première ouverture puis reste monté.
 */
export function FicheTabs({
  tabs,
  label,
  param = 'onglet',
}: {
  tabs: (FicheTab | false | null | undefined)[];
  label: string;
  param?: string;
}): ReactElement {
  return <Tabs tabs={tabs} label={label} param={param} mount="visited" />;
}

/**
 * Deux colonnes sur grand écran : contenu principal à gauche, résumé (faits clés, actions,
 * état) à droite, collant au défilement ; une seule colonne au téléphone, résumé en premier
 * si `asideFirst`.
 */
export function FicheLayout({
  children,
  aside,
  asideFirst = false,
}: {
  children: ReactNode;
  aside?: ReactNode;
  asideFirst?: boolean;
}): ReactElement {
  if (aside === undefined || aside === null || aside === false)
    return <div className="o-stack">{children}</div>;
  return (
    <div className={asideFirst ? 'o-fiche o-fiche--aside-first' : 'o-fiche'}>
      <div className="o-fiche__main o-stack">{children}</div>
      <aside className="o-fiche__aside o-stack o-stack--tight" aria-label="Résumé">
        {aside}
      </aside>
    </div>
  );
}

/**
 * Bloc du résumé latéral = `Panel variant="aside"` (alias gardé pour les écrans existants).
 * Les actions éventuelles vont en tête du bloc ; le résumé ne porte pas de formulaire.
 */
export function AsideCard({
  title,
  icon,
  children,
  actions,
}: {
  title: string;
  icon?: LucideIcon | undefined;
  children: ReactNode;
  actions?: ReactNode;
}): ReactElement {
  return (
    <Panel variant="aside" title={title} icon={icon} actions={actions} className="o-aside-card">
      {children}
    </Panel>
  );
}

/** Faits clés (libellé au-dessus de la valeur), compacts : résumé d'une fiche. */
export function KeyFacts({
  items,
}: {
  items: ([string, ReactNode] | false | null | undefined)[];
}): ReactElement {
  const shown = items.filter((i): i is [string, ReactNode] => i !== false && i != null);
  return (
    <dl className="o-facts">
      {shown.map(([label, value]) => (
        <div key={label} className="o-facts__item">
          <dt>{label}</dt>
          <dd>{value === null || value === undefined || value === '' ? '—' : value}</dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * Section repliable (détails secondaires, imports, réglages rares) : fermée par défaut, son
 * titre dit ce qu'elle contient.
 */
export function Repli({
  title,
  hint,
  icon: Icon,
  open = false,
  children,
}: {
  title: string;
  hint?: string | undefined;
  icon?: LucideIcon | undefined;
  open?: boolean;
  children: ReactNode;
}): ReactElement {
  return (
    <details className="o-repli" open={open}>
      <summary className="o-repli__summary">
        <span className="o-repli__title">
          {Icon !== undefined && <Icon aria-hidden="true" size={18} />}
          {title}
        </span>
        {hint !== undefined && <span className="o-hint">{hint}</span>}
        <ChevronDown aria-hidden="true" size={18} className="o-repli__chevron" />
      </summary>
      <div className="o-repli__body">{children}</div>
    </details>
  );
}

export interface MoreAction {
  label: string;
  icon?: LucideIcon | undefined;
  onSelect: () => void;
  danger?: boolean | undefined;
  disabled?: boolean | undefined;
}

/** Menu « … » des actions secondaires = `ActionMenu` des patrons en bouton icône. */
export function MoreMenu({
  actions,
  label = 'Autres actions',
}: {
  actions: (MoreAction | false | null | undefined)[];
  label?: string;
}): ReactElement | null {
  return (
    <ActionMenu
      iconOnly
      label={label}
      items={actions.filter((a): a is MoreAction => a !== false && a != null)}
    />
  );
}

/**
 * Filtres en pastilles = `ChipFilter` des patrons ; l'option de valeur vide devient la
 * pastille « tout » (avec son compteur dans le libellé).
 */
export function FilterPills<V extends string>({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: V | '';
  onChange: (value: V | '') => void;
  options: { value: V | ''; label: string; count?: number | undefined }[];
}): ReactElement {
  const all = options.find((o) => o.value === '');
  return (
    <ChipFilter
      label={label}
      value={value}
      onChange={(v) => {
        onChange((Array.isArray(v) ? (v[0] ?? '') : v) as V | '');
      }}
      allLabel={
        all === undefined
          ? null
          : all.count === undefined
            ? all.label
            : `${all.label} · ${String(all.count)}`
      }
      options={options
        .filter((o) => o.value !== '')
        .map((o) => ({ value: o.value, label: o.label, count: o.count }))}
    />
  );
}

export interface RelatedLink {
  label: string;
  to: string;
  icon?: LucideIcon | undefined;
  /** Nombre d'objets liés (masqué si indéfini). */
  count?: number | undefined;
  /** Précision courte (référence, état). */
  hint?: string | undefined;
  tone?: 'warning' | 'critical' | undefined;
}

/**
 * Bloc « Lié à » fait de liens directs avec compteurs = `Related` des patrons, un groupe-lien
 * par objet lié (la ligne ouvre la fiche ou la liste filtrée).
 */
export function RelatedLinks({
  links,
  title = 'Lié à',
}: {
  links: (RelatedLink | false | null | undefined)[];
  title?: string;
}): ReactElement | null {
  return (
    <Related
      title={title}
      groups={links
        .filter((l): l is RelatedLink => l !== false && l != null)
        .map((l) => ({
          label: l.label,
          icon: l.icon,
          count: l.count,
          hint: l.hint,
          tone: l.tone,
          to: l.to,
          items: [],
        }))}
    />
  );
}

/** Libellés des codes d'état les plus courants (envois, tâches, fiches). */
const STATUS_LABELS: Record<string, string> = {
  pending: 'En attente',
  queued: 'En file d’attente',
  sending: 'Envoi en cours',
  sent: 'Envoyé',
  delivered: 'Remis',
  read: 'Lu',
  failed: 'Échec',
  error: 'Erreur',
  bounced: 'Rejeté',
  cancelled: 'Annulé',
  canceled: 'Annulé',
  skipped: 'Ignoré',
  captured: 'Capturé (préproduction)',
  done: 'Terminé',
  ok: 'OK',
  active: 'Actif',
  inactive: 'Inactif',
  draft: 'Brouillon',
  open: 'Ouvert',
  closed: 'Fermé',
  scheduled: 'Programmé',
  running: 'En cours',
  succeeded: 'Réussi',
  success: 'Réussi',
  en_attente: 'En attente',
  envoye: 'Envoyé',
  envoyee: 'Envoyée',
  remis: 'Remis',
  echec: 'Échec',
  annule: 'Annulé',
  annulee: 'Annulée',
  brouillon: 'Brouillon',
  publie: 'Publié',
  publiee: 'Publiée',
  planifie: 'Planifié',
  planifiee: 'Planifiée',
  ouvert: 'Ouvert',
  ouverte: 'Ouverte',
  ferme: 'Fermé',
  fermee: 'Fermée',
  clos: 'Clos',
  close: 'Close',
  termine: 'Terminé',
  terminee: 'Terminée',
  en_cours: 'En cours',
  actif: 'Actif',
  inactif: 'Inactif',
  valide: 'Validé',
  validee: 'Validée',
  refuse: 'Refusé',
  refusee: 'Refusée',
  lu: 'Lu',
  a_arbitrer: 'À arbitrer',
  rejete: 'Rejeté',
  rejetee: 'Rejetée',
  applique: 'Appliqué',
  appliquee: 'Appliquée',
  echoue: 'Échoué',
  rembourse: 'Remboursé',
  en_retard: 'En retard',
  en_erreur: 'En erreur',
  a_faire: 'À faire',
  fait: 'Fait',
  non_lu: 'Non lu',
};

/** Libellé français d'un code d'état (sinon la clé rendue lisible). */
export function statusLabel(code: string | null | undefined): string {
  if (code === null || code === undefined || code === '') return '—';
  return STATUS_LABELS[code] ?? humanKey(code);
}

/** Libellé lisible d'une clé technique (`lastImport`, `places_libres` → « Last import »…). */
export function humanKey(key: string): string {
  const words = key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim()
    .toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** Valeur lisible : nombres et dates à la française, oui/non, listes séparées. */
export function humanValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Oui' : 'Non';
  if (typeof value === 'number') return value.toLocaleString('fr-FR');
  if (typeof value === 'string') {
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(value))
      return new Date(value).toLocaleString('fr-FR', {
        dateStyle: 'short',
        timeStyle: 'short',
        timeZone: 'Europe/Paris',
      });
    return value;
  }
  if (Array.isArray(value)) return value.map(humanValue).join(', ') || '—';
  if (typeof value === 'object')
    return Object.entries(value as Record<string, unknown>)
      .map(([k, v]) => `${humanKey(k)} : ${humanValue(v)}`)
      .join(' ; ');
  return '—';
}

/**
 * Données d'un résultat sans écran dédié (compte rendu d'action, compteurs) : libellés et
 * valeurs lisibles, jamais de JSON brut.
 */
export function HumanData({
  data,
  labels = {},
}: {
  data: unknown;
  /** Libellés connus des clés (sinon déduits de la clé). */
  labels?: Record<string, string>;
}): ReactElement {
  if (data === null || typeof data !== 'object' || Array.isArray(data))
    return <span>{humanValue(data)}</span>;
  return (
    <KeyFacts
      items={Object.entries(data as Record<string, unknown>).map(([k, v]) => [
        labels[k] ?? humanKey(k),
        humanValue(v),
      ])}
    />
  );
}
